import { useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import { useEffect, useRef, useState } from "react";
import { BulletPoint } from "@lending/components/BulletPoint/BulletPoint";
import { HeroSection } from "@lending/components/HeroSection/HeroSection";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { useProductType } from "@lending/hooks/useProductType";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import styles from "./ApplicationQuestions.module.css";
import { MLA_OPTIONS, isCoveredBorrowerValue, isMlaQuestionEnabled } from "./mlaQuestion";
import { useSaveMlaStatus } from "./useSaveMlaStatus";
import { useStore } from "../../../contexts/StoreContext";
import { useHeroBreakpoints } from "../../../hooks/useHeroBreakpoints";
import { usePageTransition } from "../../../hooks/usePageTransition";
import { EligibilityCriteriaCard } from "../EligibilitySelection/EligibilityCriteriaCard";

/**
 * Hardcoded MLA covered-borrower question screen (LNDG-1483 backport).
 *
 * Shown right after eligibility when the `mla_question` additional field is
 * enabled. The answer is stashed in the store and persisted as `mla_status`
 * application metadata at application creation, or saved straight to the
 * application when it already exists; a covered-borrower answer on a credit
 * card application ends the flow.
 */
function ApplicationQuestions() {
  const { m } = useHeroBreakpoints();
  const { l10n } = useLocalization();
  const { goNext } = useFlowNavigation();
  const applicationUuid = useCurrentApplicationUuid();
  const saveMlaStatus = useSaveMlaStatus();
  const { showErrorToast, showGenericErrorToast } = useToastsContext();
  const { transitionClass } = usePageTransition();
  const { data: storeData, updateData } = useStore();
  const { institution } = useGetInstitutionQuery();
  const routeProductType = useProductType();
  const productType = storeData.productType ?? routeProductType;
  const [selectedValue, setSelectedValue] = useState(storeData.mlaStatus ?? null);

  const hasLendingQuestions = isMlaQuestionEnabled(institution.lending_ask_additional_fields);

  const optionLabels: Record<string, string> = {
    active_duty: l10n.getString(
      "application-questions-mla-option-active-duty",
      null,
      "Active duty"
    ),
    active_duty_dependent: l10n.getString(
      "application-questions-mla-option-active-duty-dependent",
      null,
      "Active duty dependent"
    ),
    none: l10n.getString("application-questions-mla-option-none", null, "Neither in active duty"),
  };

  useEffect(() => {
    updateData({ hasLendingQuestions });
  }, [hasLendingQuestions, updateData]);

  // The screen is directly reachable by URL, so skip forward when the
  // question is not enabled for this institution. The ref guards against
  // goNext's unstable reference re-firing the effect.
  const hasSkippedRef = useRef(false);
  useEffect(() => {
    if (hasLendingQuestions || hasSkippedRef.current) return;
    hasSkippedRef.current = true;
    goNext(undefined, { replace: true });
  }, [hasLendingQuestions, goNext]);

  const handleContinue = async () => {
    if (!selectedValue || !hasLendingQuestions) return;

    updateData({ mlaStatus: selectedValue });

    // Saved even when it ends a credit card application, so evaluate refuses
    // the application if the applicant skips ahead by URL.
    if (applicationUuid && productType) {
      try {
        await saveMlaStatus(productType, applicationUuid, selectedValue);
      } catch {
        showGenericErrorToast();
        return;
      }
    }

    if (productType === "credit-card" && isCoveredBorrowerValue(selectedValue)) {
      showErrorToast(
        l10n.getString(
          "application-questions-not-eligible-toast",
          null,
          "Based on your answers, this product isn't available to apply for online. Please contact us to discuss your options."
        )
      );
      return;
    }

    goNext();
  };

  return (
    <PageLayout>
      <div className={styles.container}>
        <HeroSection
          title={l10n.getString(
            "eligibility-selection-title",
            null,
            "Check your offer in less than 2 minutes"
          )}
          content={
            m && (
              <div className={styles.bulletPoints}>
                <BulletPoint
                  text={l10n.getString(
                    "eligibility-selection-bullet-1",
                    null,
                    "No impact to your credit score"
                  )}
                />
                <BulletPoint
                  text={l10n.getString(
                    "eligibility-selection-bullet-2",
                    null,
                    "No application fees"
                  )}
                />
                <BulletPoint
                  text={l10n.getString(
                    "eligibility-selection-bullet-3",
                    null,
                    "Get your offer instantly"
                  )}
                />
              </div>
            )
          }
        />
        <div className={`${styles.pageContent} ${transitionClass}`}>
          <div className={styles.questionsContainer}>
            <div className={styles.question}>
              <div className={styles.heading}>
                {l10n.getString(
                  "application-questions-mla-label",
                  null,
                  "Do any of the following options apply to you or your co-applicant?"
                )}
              </div>
              <div className={styles.optionsContainer}>
                {MLA_OPTIONS.map((option, index) => (
                  <EligibilityCriteriaCard
                    key={option.value}
                    index={index}
                    label={optionLabels[option.value] ?? option.value}
                    followUpPlaceholder=""
                    options={[]}
                    hasFreeformFollowUp={false}
                    isSelected={selectedValue === option.value}
                    selectedOptionId={null}
                    freeformAnswer=""
                    onSelect={() => setSelectedValue(option.value)}
                    onOptionSelect={() => undefined}
                    onFreeformAnswerChange={() => undefined}
                  />
                ))}
              </div>
            </div>

            <div className={styles.continueButton}>
              {selectedValue !== null && (
                <Button
                  label={l10n.getString("button-label-next", null, "Next")}
                  onClick={() => void handleContinue()}
                />
              )}
            </div>
          </div>

          {!m && (
            <div className={styles.mobilePromotionalDetailsCard}>
              <ul className="list--reset fontSize--s">
                <li className="margin--bottom--xs">
                  <i className="narmi-icon-check fontColor--theme--primary" />
                  <span className="margin--left--xxs fontWeight--semibold">
                    {l10n.getString(
                      "eligibility-selection-bullet-1",
                      null,
                      "No impact to your credit score"
                    )}
                  </span>
                </li>
                <li className="margin--bottom--xs">
                  <i className="narmi-icon-check fontColor--theme--primary" />
                  <span className="margin--left--xxs fontWeight--semibold">
                    {l10n.getString("eligibility-selection-bullet-2", null, "No application fees")}
                  </span>
                </li>
                <li>
                  <i className="narmi-icon-check fontColor--theme--primary" />
                  <span className="margin--left--xxs fontWeight--semibold">
                    {l10n.getString(
                      "eligibility-selection-bullet-3",
                      null,
                      "Get your offer instantly"
                    )}
                  </span>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}

export { ApplicationQuestions };
